import React from "react";
import { HighlightText } from "../../components/HighlightText";
import { interpolate, spring, useVideoConfig } from "remotion";
import { BulletItem } from "../../types";
import { CompanyBadge } from "../../components/CompanyBadge";
import { detectBrandInText } from "../../components/companyDetector";

interface CorporateInsightsProps {
  bullets: BulletItem[];
  globalFrame: number;
}

export const CorporateInsights: React.FC<CorporateInsightsProps> = ({
  bullets,
  globalFrame,
}) => {
  const { fps } = useVideoConfig();
  const currentMs = (globalFrame / fps) * 1000;

  // Determine active bullet index
  let activeIndex = bullets.findIndex(
    (b) => currentMs >= b.startMs && currentMs <= b.endMs,
  );

  if (activeIndex === -1) {
    const pastBullets = bullets.filter((b) => b.endMs <= currentMs);
    activeIndex = pastBullets.length > 0 ? pastBullets.length - 1 : 0;
  }

  const activeBullet = bullets[activeIndex];
  const bulletStartFrame = (activeBullet.startMs / 1000) * fps;
  const elapsedFrames = Math.max(0, globalFrame - bulletStartFrame);

  // Slide-in from left animation
  const translateX = interpolate(elapsedFrames, [0, 12], [-30, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const textOpacity = interpolate(elapsedFrames, [0, 10], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Card entrance animation (runs once on first bullet)
  const firstBulletStart = (bullets[0].startMs / 1000) * fps;
  const cardElapsed = Math.max(0, globalFrame - firstBulletStart);

  const cardOpacity = interpolate(cardElapsed, [0, 14], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const cardScale = spring({
    frame: cardElapsed,
    fps,
    config: { damping: 18, mass: 0.55, stiffness: 110 },
  });

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        zIndex: 10,
        margin: "auto 0",
        padding: "10px 0",
      }}
    >
      <div
        style={{
          width: "100%",
          background: "#ffffff",
          borderLeft: "4px solid #2563eb",
          borderRadius: "0 8px 8px 0",
          padding: "36px 40px",
          boxShadow: "0 4px 24px rgba(26,43,94,0.08)",
          display: "flex",
          flexDirection: "column",
          gap: "20px",
          boxSizing: "border-box",
          opacity: cardOpacity,
          transform: `scale(${cardScale})`,
        }}
      >
        {/* Top: thin separator + number badge row + brand badge */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "12px",
          }}
        >
          {/* Full-width separator */}
          <div
            style={{
              width: "100%",
              height: "1px",
              background: "#e2e8f0",
            }}
          />

          {/* Number badge + brand badge */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div>
              <span
                style={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: "12px",
                  fontWeight: 700,
                  color: "#ffffff",
                  background: "#2563eb",
                  borderRadius: "4px",
                  padding: "2px 8px",
                  display: "inline-block",
                  letterSpacing: "0.5px",
                }}
              >
                #{String(activeIndex + 1).padStart(2, "0")}
              </span>
            </div>
            {(() => {
              const brand = detectBrandInText(activeBullet.text);
              return brand ? (
                <CompanyBadge
                  name={brand}
                  color="#2563eb"
                  accentColor="#2563eb"
                  size={68}
                  style="glass"
                  opacity={textOpacity}
                />
              ) : null;
            })()}
          </div>
        </div>

        {/* Progress dots */}
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          {bullets.map((_, idx) => {
            const isPastOrActive = idx <= activeIndex;
            return (
              <div
                key={idx}
                style={{
                  width: isPastOrActive ? "20px" : "8px",
                  height: "8px",
                  borderRadius: isPastOrActive ? "4px" : "50%",
                  background: isPastOrActive ? "#2563eb" : "transparent",
                  border: isPastOrActive ? "none" : "1.5px solid #cbd5e1",
                  transition: "width 0.2s ease",
                }}
              />
            );
          })}
        </div>

        {/* Bullet text */}
        <div
          style={{
            opacity: textOpacity,
            transform: `translateX(${translateX}px)`,
            minHeight: "260px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <p
            style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: "48px",
              fontWeight: 700,
              color: "#1a2b5e",
              lineHeight: 1.35,
              margin: 0,
              padding: 0,
            }}
          >
            <HighlightText
              highlightStyle={{
                fontWeight: 700,
                color: "#2563eb",
                borderBottom: "3px solid #60a5fa",
                display: "inline",
              }}
              text={activeBullet.text}
              highlights={activeBullet.highlights}
            />
          </p>
        </div>

        <div
          style={{ height: "1px", background: "#e2e8f0", marginTop: "4px" }}
        />
      </div>
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
